import React, { useState } from 'react';
import { ArrowDownIcon, CrossIcon } from './icons';
import { CheckIcon } from '../../icons';
import { Tooltip, TooltipPosition } from '../../../../components';
import { FEATURES_LIST } from './constants';
import { useMixPanel } from '../../../../components/MixPanel/useMixPanel';
import { MixpanelEvent } from '../../../../components/MixPanel/events/events';
import { useWindowSize } from '../../../../hooks';

const Features: React.FC = () => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'professional' | 'enterprise'>(
    'professional'
  );

  const { trackEvent } = useMixPanel();

  const { width } = useWindowSize();
  const isMobileView = width <= 1024;
  const mobileTableHeaderText =
    selectedTab === 'professional' ? 'Professional Features' : 'Edge Features';

  const attrs = {
    container: {
      className: 'features',
    },
    button: {
      type: 'button' as const,
      className: `features__button${
        showAllFeatures ? ' features__button--active' : ''
      }`,
      onClick: () => {
        setShowAllFeatures((prevState) => !prevState);
        if (!showAllFeatures) {
          trackEvent(MixpanelEvent.PRICING_PAGE_EXPAND_VIEW_ALL_FEATURES);
        }
      },
    },
    tableWrapper: {
      className: `features__table-wrapper${
        !showAllFeatures ? ' features__table-wrapper--hidden' : ''
      }`,
      onTransitionEnd: (e: React.TransitionEvent<HTMLDivElement>) => {
        if (showAllFeatures) {
          e.currentTarget.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      },
    },
    tableTabs: {
      className: 'features__table-tabs',
    },
    professionalTab: {
      className: 'features__table-tab',
      type: 'button' as const,
      disabled: selectedTab === 'professional',
      onClick: () => setSelectedTab('professional'),
    },
    enterpriseTab: {
      className: 'features__table-tab',
      type: 'button' as const,
      disabled: selectedTab === 'enterprise',
      onClick: () => setSelectedTab('enterprise'),
    },
    table: {
      className: `features__table`,
    },
    headerRow: {
      className: 'features__row features__row--header',
    },
    cell: {
      className: 'features__cell',
    },
  };

  const renderFeatures = () =>
    FEATURES_LIST.map(({ isFunctionality, isIncluded, name, tooltip }) => {
      const props = {
        row: {
          key: name,
          className: `features__row${
            isFunctionality ? '' : ' features__row--subheader'
          }`,
        },
        nameCell: {
          className: `features__cell${
            tooltip ? '' : ' features__cell--no-tooltip'
          }`,
        },
        tooltip: {
          text: tooltip,
          width: 256,
          contentClassName: 'features__tooltip-content',
          position: isMobileView
            ? TooltipPosition.TOP_START
            : TooltipPosition.TOP,
          positionOffset: isMobileView ? 6 : 15,
        },
      };

      return (
        <div {...props.row}>
          <div {...props.nameCell}>
            {tooltip ? <Tooltip {...props.tooltip} /> : null}
            <span>{name}</span>
          </div>
          {!isMobileView || selectedTab === 'professional' ? (
            <div {...attrs.cell}>
              {isIncluded ? <CheckIcon /> : <CrossIcon />}
            </div>
          ) : null}
          {!isMobileView || selectedTab === 'enterprise' ? (
            <div {...attrs.cell}>
              <CheckIcon />
            </div>
          ) : null}
        </div>
      );
    });

  return (
    <div {...attrs.container}>
      <button {...attrs.button}>
        <span>View All Features</span>
        <ArrowDownIcon />
      </button>
      <div {...attrs.tableWrapper}>
        {isMobileView ? (
          <div {...attrs.tableTabs}>
            <button {...attrs.professionalTab}>Professional</button>
            <button {...attrs.enterpriseTab}>Edge</button>
          </div>
        ) : null}
        <div {...attrs.table}>
          <div {...attrs.headerRow}>
            <div {...attrs.cell}>
              {isMobileView ? mobileTableHeaderText : 'Compare plans'}
            </div>
            {!isMobileView ? (
              <>
                <div {...attrs.cell}>Professional</div>
                <div {...attrs.cell}>Edge</div>
              </>
            ) : null}
          </div>
          {renderFeatures()}
        </div>
      </div>
    </div>
  );
};

export default Features;
